import { DeelTraject } from '@/types/deeltrajecten';

import { MwdDeeltraject } from '../types/Domain';

import styles from './MwdChart.module.css';

export const DRGPNT_POSITION_Y_DEFAULT = -6;
export const DRGPNT_POSITION_Y_TRAP_INZET = 16;
export const DRGPNT_POSITION_Y_KETEN_CONFLICT = 43;
export const DRGPNT_POSITION_Y_KETEN_CONFLICT_VAN_EN_NAAR = 62;
export const DRGPNT_POSITION_X_KETEN_CONFLICT_VAN_EN_NAAR = -40;
export const LABEL_POSITION_X_DEFAULT = 8;
export const LABEL_POSITION_Y_CONFLICT = 38;
export const LABEL_POSITION_Y_CONFLICT_VAN_EN_NAAR = 61;
export const LABEL_POSITION_Y_DEFAULT = 16;
export const LABEL_POSITION_Y_KETEN_CONFLICT = 42;

export const DEPARTURE_LABEL_X_SHORT = -12;

/** Get the border-radius (`rx`) to use for a deeltraject block (inzet/claim) of a given width.
 *  Very narrow blocks (e.g. inzetten/claims shorter than ~4 minutes) would otherwise turn into an
 *  oval shape when using the default border-radius, since the rounded corners overlap. Capping the
 *  radius relative to the width keeps the block looking like a (slightly) rounded rectangle instead.
 *
 * @param width The rendered width of the deeltraject block.
 * @param baseRx The default border-radius used for wide enough blocks.
 */
export const getDeeltrajectRx = (width: number, baseRx: number): number => Math.max(Math.min(baseRx, width / 3), 0);

const calculateLabelPositionX = (duration: number): number => {
  const LABEL_POSITION_X_DEFAULT = 8;
  const increments = Array.from({ length: 30 }, (_, i) => (i + 2) * 60 * 1000);
  const positions = [
    0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60, 64, 68, 72, 76, 80, 84, 88, 92, 96, 100, 104, 108, 112,
    116,
  ];

  for (let i = 0; i < increments.length; i++) {
    if (duration < increments[i]) {
      return LABEL_POSITION_X_DEFAULT + positions[i];
    }
  }

  return LABEL_POSITION_X_DEFAULT;
};

export const getVariant = (
  d: MwdDeeltraject | DeelTraject,
  isKorteInzet: boolean,
): 'primary' | 'claim' | 'defectInzetbaar' | 'korteInzet' => {
  if (isKorteInzet) return 'korteInzet';
  if (d.claimsoort) return 'claim';
  if (d.variant === 'defectInzetbaar') return 'defectInzetbaar';
  return 'primary';
};

export const label = (d: MwdDeeltraject | DeelTraject, isKorteInzet: boolean) => {
  const variant = getVariant(d, isKorteInzet);
  return `${styles.labelStyle} ${styles[`labelStyle-${variant}`]}`;
};

export const labelPositionY = (deeltraject: DeelTraject | MwdDeeltraject) => {
  if (deeltraject.inzetIsKort) {
    return LABEL_POSITION_Y_DEFAULT + deeltraject.trapHoogteIndex * 22;
  }
  if (
    deeltraject.ketenConfig.ketenIndex === 0 &&
    deeltraject.isDienstregelpuntNaarInConflict &&
    !deeltraject.isDienstregelpuntVanInConflict &&
    !deeltraject.ketenConfig.startKetenBreak
  ) {
    return LABEL_POSITION_Y_CONFLICT;
  }
  if (
    deeltraject.ketenConfig.ketenIndex === 0 &&
    deeltraject.isDienstregelpuntNaarInConflict &&
    deeltraject.isDienstregelpuntVanInConflict &&
    !deeltraject.ketenConfig.startKetenBreak
  ) {
    return LABEL_POSITION_Y_CONFLICT_VAN_EN_NAAR;
  }
  if (deeltraject.ketenConfig.ketenIndex > 0) {
    return LABEL_POSITION_Y_KETEN_CONFLICT * deeltraject.ketenConfig.ketenIndex + LABEL_POSITION_Y_DEFAULT;
  }
  if (!deeltraject.inzetIsKort) {
    return deeltraject.isDienstregelpuntNaarInConflict && !deeltraject.ketenConfig.startKetenBreak
      ? LABEL_POSITION_Y_CONFLICT
      : LABEL_POSITION_Y_DEFAULT;
  }

  return LABEL_POSITION_Y_DEFAULT;
};

export const labelPositionX = (deeltraject: DeelTraject | MwdDeeltraject): number => {
  if (deeltraject.inzetIsKort) {
    const duration = deeltraject.aankomstUitvoeringsTijd.getTime() - deeltraject.vertrekUitvoeringsTijd.getTime();
    return calculateLabelPositionX(duration);
  }
  return 8; // LABEL_POSITION_X_DEFAULT
};

export const vertrekStationLabelX = (
  deeltraject: DeelTraject | MwdDeeltraject,
  trapInzetMarginDienstregelpunt: number,
) => {
  if (deeltraject.inzetIsKort) {
    return DEPARTURE_LABEL_X_SHORT - trapInzetMarginDienstregelpunt + 8;
  }
  if (
    deeltraject.ketenConfig.ketenIndex === 0 &&
    deeltraject.isDienstregelpuntNaarInConflict &&
    deeltraject.isDienstregelpuntVanInConflict &&
    !deeltraject.ketenConfig.startKetenBreak
  ) {
    return DRGPNT_POSITION_X_KETEN_CONFLICT_VAN_EN_NAAR;
  }
  return LABEL_POSITION_X_DEFAULT;
};

export const DrgpntPositionY = (deeltraject: DeelTraject | MwdDeeltraject) => {
  if (deeltraject.inzetIsKort) {
    return DRGPNT_POSITION_Y_TRAP_INZET + deeltraject.trapHoogteIndex * 22;
  }
  if (
    deeltraject.isDienstregelpuntNaarInConflict &&
    !deeltraject.isDienstregelpuntVanInConflict &&
    deeltraject.ketenConfig.ketenIndex === 0 &&
    deeltraject.ketenConfig.indexOfConflictingInzet === 1
  ) {
    return DRGPNT_POSITION_Y_DEFAULT;
  }
  if (
    deeltraject.ketenConfig.ketenIndex === 0 &&
    deeltraject.isDienstregelpuntNaarInConflict &&
    deeltraject.isDienstregelpuntVanInConflict &&
    !deeltraject.ketenConfig.startKetenBreak
  ) {
    return DRGPNT_POSITION_Y_KETEN_CONFLICT_VAN_EN_NAAR;
  }
  if (deeltraject.ketenConfig.ketenIndex > 0) {
    return DRGPNT_POSITION_Y_KETEN_CONFLICT * deeltraject.ketenConfig.ketenIndex + DRGPNT_POSITION_Y_DEFAULT;
  }

  return DRGPNT_POSITION_Y_DEFAULT;
};
